import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import Places from 'constants/places'
import useExperienceSearchUrl from 'hooks/Experiences/useExperienceSearchUrl'
import { usePlaceByName } from 'hooks/usePlace'
import { min } from 'lib/array/arrayUtils'
import { formatRoundedAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import type { DrawerExperienceSearchTile } from '../components/ExperienceListingDrawer/ExperienceDetailView'
import { getConcreteExperienceSearchPlace } from '../components/ExperienceListingDrawer/experienceSearchUrlUtils'
import useExperienceSearch from '../components/ExperienceListingDrawer/useExperienceSearch'

const EXPERIENCE_SEARCH_PLACE_LEVELS: Array<App.PlaceType> = ['multi_city_vicinity', 'province_state', 'city']

interface UsePlaceThingsToDoOptions {
  placeName: string
  countryName?: string
  coordinates?: { lat: number; lng: number }
  enabled: boolean
}

export interface UsePlaceThingsToDoResult {
  experiences: Array<DrawerExperienceSearchTile>
  experiencesCount: number
  countLabel: string
  lowestPricedExperience: DrawerExperienceSearchTile | undefined
  formattedLowestPrice: string | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

function usePlaceThingsToDo({
  placeName,
  countryName,
  coordinates,
  enabled,
}: UsePlaceThingsToDoOptions): UsePlaceThingsToDoResult {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const currentCurrency = useContext(CurrencyContext)

  const searchQuery = useMemo(() => {
    return countryName ? `${placeName}, ${countryName}` : placeName
  }, [countryName, placeName])
  const resolvedPlace = usePlaceByName(searchQuery)
  const resolvedPlaceId = resolvedPlace?.id && resolvedPlace.id !== Places.Anywhere.id ? resolvedPlace.id : undefined
  const resolvedLatLong = useMemo(() => {
    if (!coordinates) return undefined

    return {
      latitude: coordinates.lat,
      longitude: coordinates.lng,
    }
  }, [coordinates])
  const experienceSearchUrlParams = useMemo(() => ({
    latLong: resolvedLatLong,
    placeLevels: EXPERIENCE_SEARCH_PLACE_LEVELS,
  }), [resolvedLatLong])
  const [resolvedExperienceSearchUrl, isResolvingExperienceSearchPlace] = useExperienceSearchUrl(experienceSearchUrlParams)
  const resolvedExperienceSearchPlace = useMemo(
    () => getConcreteExperienceSearchPlace(resolvedExperienceSearchUrl),
    [resolvedExperienceSearchUrl],
  )
  const searchPlaceId = resolvedPlaceId ?? resolvedExperienceSearchPlace?.placeId
  const fallbackPlaceIds = useMemo(() => {
    if (!resolvedExperienceSearchPlace || resolvedExperienceSearchPlace.placeId === searchPlaceId) {
      return undefined
    }

    return [resolvedExperienceSearchPlace.placeId]
  }, [resolvedExperienceSearchPlace, searchPlaceId])
  const isWaitingForCoordinatePlace =
    enabled &&
    !!coordinates &&
    !resolvedPlaceId &&
    isResolvingExperienceSearchPlace

  const {
    experiences,
    isLoading: isExperienceSearchLoading,
    isError,
    refetch,
  } = useExperienceSearch({
    searchQuery,
    placeId: searchPlaceId,
    fallbackPlace: resolvedExperienceSearchPlace,
    fallbackPlaceIds,
    enabled: enabled && !!searchPlaceId && !isWaitingForCoordinatePlace,
  })

  const experiencesCount = experiences.length

  const countLabel = intl.formatMessage(
    {
      id: '4DlQKlqzVYbFq6Clh35W',
      defaultMessage: '{count, plural, one {# experience available} other {# experiences available}}',
      description: 'Subtitle on the nearby experiences overview card showing how many experience offers are available for the current place.',
    },
    { count: experiencesCount },
  )

  const lowestPricedExperience = useMemo(() => {
    return min(
      experiences.filter((experience) => experience.price !== undefined),
      (experience) => experience.price ?? Number.POSITIVE_INFINITY,
    )
  }, [experiences])

  const formattedLowestPrice = useMemo(() => {
    if (lowestPricedExperience?.price === undefined) {
      return undefined
    }

    return formatRoundedAmountWithDollarType(
      lowestPricedExperience.price,
      lowestPricedExperience.currencyCode ?? currentCurrency,
      geo.currentRegionCode,
    )
  }, [lowestPricedExperience, currentCurrency, geo.currentRegionCode])

  return {
    experiences,
    experiencesCount,
    countLabel,
    lowestPricedExperience,
    formattedLowestPrice,
    isLoading: isExperienceSearchLoading || isWaitingForCoordinatePlace,
    isError,
    refetch,
  }
}

export default usePlaceThingsToDo
